"use client"

import { useEffect, useState } from "react"
import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { CardV2, CardHeader, CardContent, CardFooter, CardTitle } from "../../components/CardV2/CardV2"
import { ButtonV2 } from "../../components/ButtonV2/ButtonV2"
import { Skeleton } from "../../components/Skeleton/Skeleton"
import { MapPin, Phone, Mail, RefreshCcw, AlertCircle } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Instagram } from "lucide-react"
import BookingAPI, { PendingBookStatus } from "../../api/BookingAPI"
import { BookingFeedback } from "../../components/BookingFeedback/BookingFeedback"
import { languageToLocale } from "../../shared/supported-languages"
import { ContactInfoItem } from "../../components/ContactInfoItem/ContactInfoItem"

interface CancelReservationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  t: (key: string) => string
}

export function CancelReservationModal({ isOpen, onClose, onConfirm, t }: CancelReservationModalProps) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <CardV2 className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-destructive" />
            {t("bookings.reservation.cancel")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>{t("bookings.reservation.cancel.warning")}.</p>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <ButtonV2 variant="outline" onClick={onClose}>
            {t("bookings.reservation.cancel.warning.decline")}
          </ButtonV2>
          <ButtonV2 variant="destructive" onClick={onConfirm}>
            {t("bookings.reservation.cancel.warning.confirm")}
          </ButtonV2>
        </CardFooter>
      </CardV2>
    </div>
  )
}

export default function ReservationDetailsPage() {
  const { t, i18n } = useTranslation()
  const { bookingWidgetId, reservationId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [reservation, setReservation] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [bookingStatus, setBookingStatus] = useState<PendingBookStatus>()
  const [refreshing, setRefreshing] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshCount, setRefreshCount] = useState(0)
  const [isRefreshButtonLoading, setIsRefreshButtonLoading] = useState(false)
  const MAX_REFRESH_COUNT = 20 // Will refresh for ~5 min (20 * 15 seconds)
  const REFRESH_INTERVAL = 15000 // 15 seconds

  // Check for autoRefresh query parameter - simplified to use just one parameter
  useEffect(() => {
    const shouldAutoRefresh = searchParams.get('autoRefresh') === 'true'
    setAutoRefresh(shouldAutoRefresh)
  }, [searchParams])

  // Handle auto-refresh functionality
  useEffect(() => {
    let refreshTimer: NodeJS.Timeout | null = null

    if (autoRefresh && reservation?.paymentStatus === 'PENDING' && refreshCount < MAX_REFRESH_COUNT) {
      refreshTimer = setTimeout(() => {
        fetchReservationDetails()
        setRefreshCount(prev => prev + 1)
      }, REFRESH_INTERVAL)
    }

    // If payment is completed or failed definitively, stop auto-refresh and clear query param
    if (autoRefresh && reservation?.paymentStatus && ['SUCCEEDED', 'FAILED', 'DISPUTED', 'CANCELED'].includes(reservation.paymentStatus)) {
      setAutoRefresh(false)
      // Remove the query parameter by navigating to the same page without it
      navigate(`/booking/${bookingWidgetId}/reservation/${reservationId}`, { replace: true })
    }

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, reservation, refreshCount, bookingWidgetId, reservationId, navigate])

  const fetchReservationDetails = async () => {
    setRefreshing(true)
    try {
      const response = await BookingAPI.getReservationDetails(bookingWidgetId!, reservationId!);

      // Keep the original API response structure to match what BookingFeedback expects
      setBookingStatus(response.data.pendingBooking.status);
      setReservation(response.data); // Store the raw API response
    } catch (err: any) {
      setError(`${t("bookings.reservation.not_found")!}.`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!reservationId) {
      setError(`${t("bookings.reservation.missing_reservation_id")!}.`);
      setLoading(false);
      return;
    }
  
    fetchReservationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingWidgetId, reservationId, t]);

  const handleCancelReservation = () => {
    setIsModalOpen(true)
  }

  const confirmCancellation = async () => {
    setIsModalOpen(false);
    try {
      await BookingAPI.cancelReservation(bookingWidgetId!, reservationId!);
      setBookingStatus("CANCELED_BY_BRIDE");
      await fetchReservationDetails();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRetryPayment = async () => {
    const language = i18n.language;
    const locale = languageToLocale(language);

    try {
      const response = await BookingAPI.createPaymentSessionForExistingBooking(
        bookingWidgetId!,
        reservationId!,
        locale
      );

      if (response.data?.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      } else {
        setError(t("booking.payment.create_session_failed")!);
      }
    } catch (err: any) {
      setError(err.message || t("booking.payment.create_session_failed"));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100">
        <Skeleton className="h-10 w-10" />
      </div>
    )
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-100">
      <CardV2 className="w-full max-w-4xl">
        {autoRefresh && reservation?.paymentStatus === 'PENDING' && (
          <div className="bg-blue-50 p-2 text-center text-sm text-blue-700 rounded-t-lg border-b border-blue-100">
            {t("booking.payment.auto_refreshing")}
            <RefreshCcw className="inline-block w-4 h-4 ml-1 animate-spin" />
          </div>
        )}
        <div className="grid md:grid-cols-3">
          <div className="md:col-span-2">
            <CardHeader>
              {/*  */}
            </CardHeader>
            <CardContent className="space-y-6">
              {error ? (
                <div className="bg-primary/10 rounded-lg p-6 space-y-4">
                  <p>{error}</p>
                  {refreshing && (
                    <div className="flex justify-center">
                      <RefreshCcw className="animate-spin h-6 w-6 text-primary" />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <BookingFeedback
                    state={
                      bookingStatus === "REJECTED" || bookingStatus === "CANCELED_BY_BRIDE"
                      ? "failure"
                      : bookingStatus === "PENDING"
                        ? "pending"
                        : "success"
                    }
                    bookingDetails={reservation}
                    storeName={reservation.storeDetails?.name}
                    storeAddress={reservation.storeDetails?.address}
                    contactPhone={reservation.storeDetails?.contactPhone}
                    onRetry={fetchReservationDetails}
                    onRetryPayment={
                      (['PENDING', 'FAILED', 'CANCELED'].includes(reservation?.paymentStatus?.status)) &&
                      reservation?.bookingPlan?.useOnlinePayments !== false
                        ? handleRetryPayment 
                        : undefined
                    }
                    failureReason={error || undefined}
                  />
                </>
              )}
            </CardContent>
  
            <CardFooter className="flex justify-center flex-col space-y-1">
              {!error && (
                <>
                  {/* Show cancellation info based on cancellation possibility */}
                  {reservation.canCancel ? (
                    <p>{`${t("bookings.reservation.cancel.limit")}.`}</p>
                  ) : (
                    <p className="text-center text-sm text-muted-foreground mb-2">
                      {t("bookings.reservation.contact_to_cancel",
                        "To cancel this reservation, please reach out to the store directly by phone, email, or social channels.")}
                    </p>
                  )}

                  <div className="flex gap-2 justify-center">
                    {/* Only show cancel button if canCancel is true */}
                    {reservation.canCancel && (
                      <ButtonV2
                        onClick={handleCancelReservation}
                        disabled={
                          bookingStatus === "CANCELED_BY_BRIDE" ||
                          bookingStatus === "REJECTED"
                        }
                        variant="destructive"
                      >
                        {bookingStatus === "CANCELED_BY_BRIDE" || bookingStatus === "REJECTED"
                          ? t("booking.feedback.booking_status.cancelled")
                          : t("bookings.reservation.cancel")}
                      </ButtonV2>
                    )}
                    <ButtonV2
                      onClick={() => {
                        setIsRefreshButtonLoading(true);
                        fetchReservationDetails().finally(() => {
                          setIsRefreshButtonLoading(false);
                        });
                      }}
                      variant="outline"
                      disabled={isRefreshButtonLoading}
                    >
                      <RefreshCcw className={`w-4 h-4 mr-2 ${isRefreshButtonLoading ? 'animate-spin' : ''}`} />
                      {isRefreshButtonLoading ? t("global.loading") : t("global.refresh")}
                    </ButtonV2>
                  </div>
                </>
              )}
            </CardFooter>
          </div>
  
          <div className="md:col-span-1 bg-gray-50 p-6 rounded-r-lg">
            {!error && (
              <>
                <div className="text-center">
                                  {reservation.storeDetails?.logoUrl && (
                  <img src={reservation.storeDetails.logoUrl} alt="Store Logo" className="w-[100px] mx-auto rounded-full" />
                )}
                  <h2 className="mt-4 text-xl font-semibold">{reservation.storeDetails?.name}</h2>
                </div>
                <div className="space-y-2 mt-4">
                  <ContactInfoItem icon={Phone} value={reservation.storeDetails?.contactPhone} />
                  <ContactInfoItem icon={Mail} value={reservation.storeDetails?.contactEmail} />
                  <ContactInfoItem icon={MapPin} value={reservation.storeDetails?.address} />
                  <ContactInfoItem icon={Instagram} value={reservation.storeDetails?.instagram} />
                </div>
              </>
            )}
          </div>
        </div>
      </CardV2>
  
      <CancelReservationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmCancellation}
        t={t}
      />
    </div>
  )
}